import React, { useCallback, useMemo } from 'react';
import type { Selector } from 'react-redux';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { qs } from 'url-parse';

import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import { Header } from '@swf/core/enums';
import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { TryAgainMessage } from '@swf/sports/components';
import type { SelectItem } from '@swf/sports/components/Select/Select';
import useScoreboardPolling from '@swf/sports/hooks/useScoreboardPolling/useScoreboardPolling';
import { getEventTypesWithInPlayEventIds } from '@swf/sports/utils/getEventTypesWithInPlayEventIds';
import getEventsFromMarketTemplateData from '@swf/sports/utils/getEventsFromMarketTemplateData';

import styles from './SportsMatchFilteringWidget.scss';
import Filters from './components/Filters';
import Results from './components/Results';
import { sportsMatchFilteringWidgetModel, useSportsMatchFilteringWidget } from './model';

const SportsMatchFilteringWidget: WidgetFC = () => {
  const history = useHistory();
  const { widgetDispatch, widgetSelect } = useSportsMatchFilteringWidget();
  const events = useSelector(widgetSelect.events);
  const eventTypes = useSelector(widgetSelect.eventTypes);
  const markets = useSelector(widgetSelect.markets);
  const competitions = useSelector(widgetSelect.competitions);
  const isErroneous = useSelector(widgetSelect.errorCode);
  const pathname = useSelector(select.location.stripPrefix as Selector<RootState, string>);
  const searchParams = useSelector(select.location.getQuery);

  const eventTypesWithEventIds = useMemo(
    () =>
      getEventTypesWithInPlayEventIds(
        getEventsFromMarketTemplateData(events, markets, competitions, eventTypes)
      ),
    [competitions, eventTypes, events, markets]
  );

  useScoreboardPolling(eventTypesWithEventIds);

  const onChangeHandler = useCallback(
    (selectItem: SelectItem<string>, param: string) => {
      if (searchParams[param] === selectItem.value) return;
      const params = { ...searchParams, [param]: selectItem.value };
      history.push({ pathname, search: '?' + qs.stringify(params) });

      const { duration, market } = params;
      widgetDispatch.matchFiltersRequested({ duration, market });
    },
    [history, pathname, searchParams, widgetDispatch]
  );

  if (isErroneous) {
    // TODO: GSFES-382 - Better polling errors
    // If no data - initial load failed - full widget error
    // If data - polling failed - show data with warning about stale prices
    return <TryAgainMessage />;
  }

  return (
    <>
      <Vary by={[Header.TimeZone]} />
      <Filters onChangeHandler={onChangeHandler} />
      <Results />
    </>
  );
};

SportsMatchFilteringWidget.model = sportsMatchFilteringWidgetModel;

export default withWidgetTestId(SportsMatchFilteringWidget, 'sports-match-filtering', {
  className: styles.wrapper
});
